import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getRole } from "../lib/auth";
import { Status } from "../components/ui";
import { PDDayRow } from "../pddays/PDDayRow";
import { TeacherPDDayCard } from "../pddays/TeacherPDDayCard";
import { usePDDays } from "../pddays/usePDDays";
import { useProfile } from "../profile/useProfile";
import type { PDDayGroup } from "../pddays/PDDayRow";
import type { PDDaySummary } from "../types";

function Section({
  titleKey,
  days,
  group,
  dayNumbers,
}: {
  titleKey: string;
  days: PDDaySummary[];
  group: PDDayGroup;
  dayNumbers: Record<string, number>;
}) {
  const { t } = useTranslation();
  if (days.length === 0) return null;
  const Card = getRole() === "teacher" ? TeacherPDDayCard : PDDayRow;
  return (
    <section className="flex flex-col gap-3">
      <h2 className="px-1 text-[11px] font-semibold uppercase tracking-wider text-app-muted">
        {t(titleKey)}
      </h2>
      {days.map((day) => (
        <Card key={day.id} day={day} group={group} dayNumber={dayNumbers[day.id]} />
      ))}
    </section>
  );
}

// The teacher's greeting above their PD-day cards.
// It greets the teacher by name; a teacher with no name on record, or a
// profile that failed to load, gets the generic greeting. The heading stays
// invisible until the profile settles so the generic text never flashes.
function PDDaysIntro() {
  const { t } = useTranslation();
  const { me, loading } = useProfile();
  const name = me?.name?.trim();
  return (
    <h1
      className={`text-xl font-semibold tracking-tight text-app-ink ${loading ? "invisible" : ""}`}
    >
      {name ? t("pdDaysWelcomeNamed", { name }) : t("pdDaysWelcomeTeacher")}
    </h1>
  );
}

// The facilitator's landing screen: every session assigned to them, and
// nothing picked on their behalf.
// Past accumulates for the life of the account; only the most recent few
// are useful at a glance, so the rest stay one tap away.
const PAST_PREVIEW = 3;

// Only the next PD Day is shown ahead; later ones appear as it passes.
function nextDay(days: PDDaySummary[]): PDDaySummary[] {
  const next = days.reduce<PDDaySummary | null>(
    (soonest, day) =>
      !soonest || new Date(day.start).getTime() < new Date(soonest.start).getTime() ? day : soonest,
    null
  );
  return next ? [next] : [];
}

export function PDDays() {
  const { t } = useTranslation();
  const [showAllPast, setShowAllPast] = useState(false);
  const { groups, total, dayNumbers, loading, error } = usePDDays();

  if (loading) {
    return <Status variant="loading" title={t("loading")} message={t("loadingPDDays")} />;
  }
  if (error) {
    return <Status variant="error" title={t("pdDaysUnavailable")} message={error} />;
  }
  if (!groups || total === 0) {
    return (
      <Status variant="empty" title={t("noAssignedPDDay")} message={t("noAssignedPDDayHelp")} />
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-6 pt-2">
      {getRole() === "teacher" ? (
        <PDDaysIntro />
      ) : (
        <div className="flex flex-col">
          <h1 className="text-2xl font-bold tracking-tight text-app-ink">{t("yourPDDays")}</h1>
          {/* <p className="mt-0.5 text-sm text-app-muted">{t("yourPDDaysHelp")}</p> */}
        </div>
      )}

      <Section
        titleKey="todaySection"
        days={groups.active}
        group="active"
        dayNumbers={dayNumbers}
      />
      <Section
        titleKey="upcomingSection"
        days={nextDay(groups.future)}
        group="future"
        dayNumbers={dayNumbers}
      />
      <Section
        titleKey="pastSection"
        days={showAllPast ? groups.past : groups.past.slice(0, PAST_PREVIEW)}
        group="past"
        dayNumbers={dayNumbers}
      />
      {groups.past.length > PAST_PREVIEW && (
        <button
          type="button"
          className="-mt-3 self-start px-1 text-sm font-semibold text-accent"
          onClick={() => setShowAllPast((value) => !value)}
        >
          {showAllPast
            ? t("showFewerPast")
            : t("showAllPast", { n: groups.past.length - PAST_PREVIEW })}
        </button>
      )}
    </div>
  );
}
